/**
 * Options for the Notifications page's "Bot type" and "Robot" filter dropdowns.
 *
 * Both derive from one `GET /api/portal/robots` call: it is already scoped to
 * this user's assigned robots, so the type list is "the types I actually own"
 * rather than every type on the platform.
 */
import { useState, useEffect, useMemo } from "react";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";

const ALL_BOT_TYPES = { value: "", label: "All bot types" };
const ALL_ROBOTS = { value: "", label: "All robots" };

// Types come back as the lowercase key (`aero`); the picker shows them cased.
const titleCase = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const useRobotFilterOptions = () => {
  const [robots, setRobots] = useState([]);

  useEffect(() => {
    let cancelled = false;

    apiFetch(`${CONFIG.BASE_URL}/api/portal/robots`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("portalToken")}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!cancelled) setRobots(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        // Filters stay at "All …" — see the header note.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Deduped, since many robots share a type.
  const botTypeOptions = useMemo(() => {
    const types = [...new Set(robots.map((r) => r.roverType).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b)
    );
    return [ALL_BOT_TYPES, ...types.map((t) => ({ value: t, label: titleCase(t) }))];
  }, [robots]);

  // Labelled by name, keyed by serialID — the value the feed actually filters
  // on. The serial is appended because robot names are not unique, and two
  // "Lobby Bot" entries would otherwise be indistinguishable in the list.
  const robotOptions = useMemo(
    () => [
      ALL_ROBOTS,
      ...robots
        .filter((r) => r.serialID)
        .map((r) => ({
          value: r.serialID,
          label: r.name ? `${r.name} · ${r.serialID}` : r.serialID,
        })),
    ],
    [robots]
  );

  return { botTypeOptions, robotOptions };
};

export default useRobotFilterOptions;
